import { useActivityLog } from '@/domains/jobs/hooks/useActivityLog';
import { useJobDocuments } from '@/domains/jobs/hooks/useJobDocuments';
import { useUploadJobDocument } from '@/domains/jobs/hooks/useUploadJobDocument';
import { describeAuditEvent } from '@/domains/settings';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import type { AuditEventType } from '@skewb-works/contracts';
import {
  Briefcase,
  CheckCircle2,
  Download,
  Edit,
  Eye,
  FileText,
  History,
  Plus,
  Shield,
  Trash2,
  Upload,
  UserPlus,
  X,
} from 'lucide-react';
import { useState } from 'react';

// ============================================================================
// ACTIVITY LOG TAB
// ============================================================================

function getActivityIcon(type: AuditEventType) {
  switch (type) {
    case 'status_change':
      return {
        icon: CheckCircle2,
        color: 'text-green-600',
        bgColor: 'bg-green-50 dark:bg-green-950',
      };
    case 'assignment':
      return { icon: UserPlus, color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-950' };
    case 'field_update':
      return { icon: Edit, color: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-950' };
    case 'completion':
      return {
        icon: CheckCircle2,
        color: 'text-green-600',
        bgColor: 'bg-green-50 dark:bg-green-950',
      };
    case 'authority_override':
      return { icon: Shield, color: 'text-red-600', bgColor: 'bg-red-50 dark:bg-red-950' };
    case 'creation':
      return { icon: Plus, color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-950' };
    case 'deletion':
      return { icon: Trash2, color: 'text-red-600', bgColor: 'bg-red-50 dark:bg-red-950' };
    default:
      return { icon: Briefcase, color: 'text-slate-600', bgColor: 'bg-slate-50 dark:bg-slate-900' };
  }
}

export function ActivityLogTab({
  jobId,
  jobData: _jobData,
}: { jobId: string; jobData: Record<string, unknown> }) {
  const { data, isLoading } = useActivityLog(Number(jobId));

  const activityLog = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E84A2B]" />
            <span className="ml-3 text-muted-foreground text-sm">Loading activity log...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
            <History className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Activity Log</h3>
            <p className="text-xs text-muted-foreground">
              Complete history of job changes and updates
            </p>
          </div>
        </div>

        {activityLog.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Activity Yet</h3>
            <p className="text-sm text-muted-foreground">
              Activity will appear here as changes are made to this job.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {activityLog.map((item, index) => {
              const { icon: Icon, color, bgColor } = getActivityIcon(item.event_type);

              return (
                <div key={item.id} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full ${bgColor} flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon className={`w-5 h-5 ${color}`} />
                    </div>
                    {index < activityLog.length - 1 && (
                      <div className="w-0.5 flex-1 min-h-[24px] bg-border mt-2" />
                    )}
                  </div>

                  <div className="flex-1 pb-3">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <div className="font-semibold text-foreground text-sm">
                          {describeAuditEvent(item)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          by {item.username} •{' '}
                          {new Date(item.created_at).toLocaleString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                    {item.reason && (
                      <p className="text-sm text-muted-foreground mt-2">{item.reason}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// DOCUMENTS TAB
// ============================================================================
export function DocumentsTab({ jobId }: { jobId: string }) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadReference, setUploadReference] = useState('');

  const numericJobId = Number(jobId);
  const { data, isLoading } = useJobDocuments(numericJobId);
  const uploadMutation = useUploadJobDocument(numericJobId);

  const documents = data?.data ?? [];

  const getCategoryBadge = (fileCategory: number | null) => {
    switch (fileCategory) {
      case 1:
        return (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-600 dark:bg-blue-950/30"
          >
            Survey
          </Badge>
        );
      case 2:
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-600 dark:bg-red-950/30"
          >
            Safety
          </Badge>
        );
      case 3:
        return (
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-600 dark:bg-purple-950/30"
          >
            Legal
          </Badge>
        );
      case 4:
        return (
          <Badge
            variant="outline"
            className="bg-orange-50 text-orange-700 border-orange-600 dark:bg-orange-950/30"
          >
            Technical
          </Badge>
        );
      case 5:
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-600 dark:bg-green-950/30"
          >
            Media
          </Badge>
        );
      default:
        return <Badge variant="outline">Other</Badge>;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    try {
      for (const file of uploadFiles) {
        await uploadMutation.mutateAsync({ file });
      }
      showSuccessToast(
        uploadFiles.length === 1 ? 'Document uploaded' : `${uploadFiles.length} documents uploaded`,
      );
      setShowUploadModal(false);
      setUploadFiles([]);
      setUploadReference('');
    } catch {
      // Leave the modal open — global mutation error handler shows the toast.
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E84A2B]" />
            <span className="ml-3 text-muted-foreground text-sm">Loading documents...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Documents</h3>
                <p className="text-xs text-muted-foreground">
                  {documents.length} file{documents.length !== 1 ? 's' : ''} attached to this job
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
              onClick={() => setShowUploadModal(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>

          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Documents Yet</h3>
              <p className="text-sm text-muted-foreground">
                Upload documents to attach them to this job.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors group"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-background border border-border flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground text-sm truncate">
                        {doc.name ?? 'Unnamed Document'}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        {doc.type && (
                          <>
                            <span>{doc.type}</span>
                            <span>•</span>
                          </>
                        )}
                        {doc.created_by && (
                          <>
                            <span>Uploaded by {doc.created_by}</span>
                            <span>•</span>
                          </>
                        )}
                        {doc.created_time && (
                          <span>
                            {new Date(doc.created_time).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getCategoryBadge(doc.file_category)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-96">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Upload Document</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowUploadModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <Input
                type="file"
                multiple
                onChange={handleFileChange}
                className="border border-border rounded-lg p-2"
              />
              <Input
                type="text"
                placeholder="Reference"
                value={uploadReference}
                onChange={(e) => setUploadReference(e.target.value)}
                className="border border-border rounded-lg p-2"
              />
            </div>
            <div className="flex justify-end mt-4 gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFiles([]);
                  setUploadReference('');
                }}
                disabled={uploadMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
                onClick={handleUpload}
                disabled={uploadMutation.isPending || uploadFiles.length === 0}
              >
                {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
